/**
 * RecentViews.tsx - Recent Views Component
 * 
 * Displays list of recently accessed views/items
 */

import React from 'react';
import { useRecentViews } from '../useNavigatr';
import './RecentViews.css';

export interface RecentViewsProps {
  /** App name for Navigatr instance */
  appName: string;
  
  /** Type of items to show (e.g., 'view', 'table', 'file') */
  type: string;
  
  /** Maximum number of items to show */
  limit?: number;
  
  /** Callback when item is clicked */
  onNavigate?: (item: any) => void;
  
  /** Custom render function for items */
  renderItem?: (item: any, index: number) => React.ReactNode;
  
  /** Custom class name */
  className?: string;
  
  /** Show as dropdown menu */
  dropdown?: boolean;
  
  /** Dropdown button label */
  dropdownLabel?: string;
}

export const RecentViews: React.FC<RecentViewsProps> = ({
  appName,
  type,
  limit = 10,
  onNavigate,
  renderItem,
  className = '',
  dropdown = false,
  dropdownLabel = 'Recent',
}) => {
  const recentItems = useRecentViews(appName, type, limit);
  const [isOpen, setIsOpen] = React.useState(false);

  if (!recentItems || recentItems.length === 0) {
    return null;
  }

  const handleClick = (item: any) => {
    if (onNavigate) {
      onNavigate(item);
    }
    if (dropdown) {
      setIsOpen(false);
    }
  };

  const defaultRenderItem = (item: any, index: number) => (
    <button
      key={index}
      className="recent-item"
      onClick={() => handleClick(item)}
    >
      <span className="recent-item-label">
        {item.data?.name || item.data?.title || item.type || 'Untitled'}
      </span>
      <span className="recent-item-time">
        {new Date(item.timestamp).toLocaleTimeString()}
      </span>
    </button>
  );

  const renderFn = renderItem || defaultRenderItem;

  if (dropdown) {
    return (
      <div className={`recent-views recent-views-dropdown ${className}`}>
        <button
          className="recent-views-toggle"
          onClick={() => setIsOpen(!isOpen)}
          aria-expanded={isOpen}
          aria-haspopup="true"
        >
          {dropdownLabel}
          <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
            <path d="M2 4l4 4 4-4H2z" />
          </svg>
        </button>
        
        {isOpen && (
          <div className="recent-views-menu">
            {recentItems.map((item, index) => renderFn(item, index))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`recent-views ${className}`}>
      <div className="recent-views-list">
        {recentItems.map((item, index) => renderFn(item, index))}
      </div>
    </div>
  );
};
